import { useEffect, useMemo } from "react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { AlertCircle, ArrowLeft, Bot } from "lucide-react"
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom"

import { useAdminResource } from "../hooks/use-admin-resource"
import { useAdminStore } from "../store/admin-store"
import type { Agent } from "../types"
import { AdminBreadcrumbs } from "./admin-breadcrumbs"
import { AdminPageContainer } from "./admin-page-container"

/**
 * Layout para páginas de detalhes do agente com sistema de abas
 */
export function AgentDetailLayout() {
  const { userId, agentId } = useParams<{ userId: string; agentId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { currentUser, currentOrganization } = useAdminStore()

  // Determinar a aba ativa baseada na rota atual
  const activeTab = useMemo(() => {
    const pathname = location.pathname
    if (pathname.endsWith("/attributes")) return "attributes"
    if (pathname.endsWith("/tools")) return "tools"
    if (pathname.endsWith("/prompts") || pathname.endsWith("/composition")) return "prompts"
    if (pathname.endsWith("/timing")) return "timing"
    return "general" // default - aba unificada
  }, [location.pathname])

  // Memoizar filtros para evitar re-renders desnecessários
  const filters = useMemo(() => {
    return agentId ? { id: `eq.${agentId}` } : {}
  }, [agentId])

  const { data: agents, loading, error } = useAdminResource<Agent>("agents", filters)

  const agent = agents?.[0]

  useEffect(() => {
    if (agent && currentUser) {
      // Aqui você pode atualizar os breadcrumbs no store se necessário
    }
  }, [agent, currentUser, userId])

  const handleBack = () => {
    navigate(`/admin/users/${userId}/agents`)
  }

  const handleTabChange = (tabValue: string) => {
    navigate(`/admin/users/${userId}/agents/${agentId}/${tabValue}`)
  }

  if (loading) {
    return (
      <AdminPageContainer
        title="Carregando..."
        subtitle="Obtendo informações do agente"
        showBackButton
        backTo={`/admin/users/${userId}/agents`}
      >
        <div className="space-y-6">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-12 w-full" />
          <div className="space-y-4">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-6 w-1/2" />
          </div>
        </div>
      </AdminPageContainer>
    )
  }

  if (error || !agent) {
    return (
      <AdminPageContainer
        title="Erro"
        subtitle="Não foi possível carregar as informações do agente"
        showBackButton
        backTo={`/admin/users/${userId}/agents`}
      >
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error?.message || "Agente não encontrado"}</AlertDescription>
        </Alert>
      </AdminPageContainer>
    )
  }

  return (
    <AdminPageContainer
      title={agent.name}
      subtitle={
        currentOrganization
          ? `Agente da organização: ${currentOrganization.name}`
          : "Detalhes do agente"
      }
      showBackButton
      backTo={`/admin/users/${userId}/agents`}
    >
      <div className="space-y-6">
        {/* Breadcrumbs */}
        <AdminBreadcrumbs
          items={[
            { label: "Admin", href: "/admin" },
            { label: "Usuários", href: "/admin/users" },
            { label: currentUser?.name || "Usuário", href: `/admin/users/${userId}` },
            { label: "Agentes", href: `/admin/users/${userId}/agents` },
            { label: agent.name, isActive: true },
          ]}
        />

        {/* Header com informações do agente */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-lg">
                  <Bot className="text-primary h-6 w-6" />
                </div>
                <div>
                  <CardTitle className="text-xl">{agent.name}</CardTitle>
                  <CardDescription className="mt-1">
                    {agent.description || "Agente de IA personalizado"}
                  </CardDescription>
                  <div className="mt-3 flex gap-2">
                    <Badge variant={agent.is_active === 1 ? "default" : "secondary"}>
                      {agent.is_active === 1 ? "Ativo" : "Inativo"}
                    </Badge>
                    <Badge variant="outline">{agent.objective}</Badge>
                    <Badge variant="secondary">{agent.communication_style}</Badge>
                  </div>
                  <div className="mt-3 space-y-1">
                    <p className="text-muted-foreground text-xs">
                      Criado em: {new Date(agent.created_at).toLocaleString("pt-BR")}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      Atualizado em:{" "}
                      {agent.updated_at
                        ? new Date(
                            typeof agent.updated_at === "string"
                              ? agent.updated_at
                              : agent.updated_at.$date
                          ).toLocaleString("pt-BR")
                        : "Não disponível"}
                    </p>
                  </div>
                </div>
              </div>
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Voltar
              </Button>
            </div>
          </CardHeader>
        </Card>

        {/* Navegação por abas */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="general">Geral</TabsTrigger>
            <TabsTrigger value="attributes">Atributos</TabsTrigger>
            <TabsTrigger value="tools">Ferramentas</TabsTrigger>
            <TabsTrigger value="prompts">Prompts</TabsTrigger>
            <TabsTrigger value="timing">Timing</TabsTrigger>
          </TabsList>

          {/* Conteúdo das abas será renderizado via Outlet com context */}
          <div className="min-h-96">
            <Outlet context={{ agent, activeTab }} />
          </div>
        </Tabs>
      </div>
    </AdminPageContainer>
  )
}
